// Panel 6 — Entrada a Pits (mockup aprobado 2026-09, pedido Robotpack).
//
// Promedio de minutos por EVENTO de paro para las causales configuradas en
// `metas_entrada_pits` (ej. Cambio de Rollo meta 4 min), acotado al DÍA
// OPERATIVO completo — no al turno en curso, mismo criterio que el ranking de
// supervisores. Una columna por causal: KPI grande del promedio vs meta +
// líderes con eventos hoy, cada uno con barra proporcional contra el marcador
// vertical de meta.
//
// Escala de la barra: el riel representa 1,6 × meta (tope) → el marcador de
// meta queda fijo al 62,5% del riel, como en el mockup. Verde dentro de meta,
// rojo por encima; el marcador usa el ámbar de advertencia (--warn).

import type { PantallaDatos, PantallaPitsCausal } from 'schemas';
import { nombreCorto, PanelVacio, TituloPanel } from '../shared';

/** Coma decimal es-CO con 1 decimal fijo (lectura de TV: "3,6"). */
const fmt1 = new Intl.NumberFormat('es-CO', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Tope del riel = 1,6 × meta → la marca de meta cae al 62,5% (mockup). */
const ESCALA_TOPE = 1.6;
const POS_META_PCT = (1 / ESCALA_TOPE) * 100; // 62,5%

const MAX_LIDERES = 5;

function colorVsMeta(promedio: number, meta: number): string {
  return promedio <= meta ? 'var(--ok)' : 'var(--crit)';
}

function FilaLider({
  nombre,
  apellido,
  promedio,
  meta,
}: {
  nombre: string;
  apellido: string;
  promedio: number;
  meta: number;
}) {
  const ancho = Math.min((promedio / (meta * ESCALA_TOPE)) * 100, 100);
  const color = colorVsMeta(promedio, meta);
  return (
    <div className="grid grid-cols-[minmax(7rem,38%)_minmax(0,1fr)_4.5rem] items-center gap-x-3 lg:gap-x-4">
      <span className="truncate text-[clamp(1.2rem,1.7vw,1.9rem)] text-fg">
        {nombreCorto(nombre, apellido)}
      </span>
      <span className="relative block h-[clamp(0.6rem,1.2vh,0.9rem)] rounded-full bg-surface-overlay">
        <span
          className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out"
          style={{ width: `${ancho}%`, background: color }}
        />
        <span
          aria-hidden="true"
          className="absolute -inset-y-1 w-[3px] rounded-sm"
          style={{ left: `${POS_META_PCT}%`, background: 'var(--warn)' }}
        />
      </span>
      <span
        className="text-right text-[clamp(1.2rem,1.7vw,1.9rem)] font-medium"
        style={{ color, fontVariantNumeric: 'tabular-nums' }}
      >
        {fmt1.format(promedio)}
      </span>
    </div>
  );
}

function ColumnaCausal({ causal }: { causal: PantallaPitsCausal }) {
  const visibles = causal.lideres.slice(0, MAX_LIDERES);
  const restantes = causal.lideres.length - visibles.length;
  const sinEventos = causal.eventos_count === 0;
  const colorKpi =
    causal.promedio_minutos === null
      ? 'var(--fg-tertiary)'
      : colorVsMeta(causal.promedio_minutos, causal.meta_minutos);

  return (
    <div className="flex min-h-0 flex-col rounded-xl border border-border bg-surface-elevated px-[1.6vw] py-[2.2vh]">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="truncate text-[clamp(1.2rem,1.6vw,1.8rem)] font-medium text-fg">
          {causal.causal_nombre}
        </h2>
        <span
          className="whitespace-nowrap rounded-full border px-3 py-0.5 text-[clamp(0.8rem,1vw,1.05rem)]"
          style={{
            color: 'var(--warn)',
            borderColor: 'var(--warn)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          Meta {fmt1.format(causal.meta_minutos)} min
        </span>
      </div>

      <div className="mb-[2vh] mt-[1.4vh] flex items-baseline gap-4">
        <span
          className="text-[clamp(2.6rem,4.4vw,4.8rem)] font-semibold leading-none"
          style={{ color: colorKpi, fontVariantNumeric: 'tabular-nums' }}
        >
          {causal.promedio_minutos === null ? '—' : fmt1.format(causal.promedio_minutos)}
        </span>
        <span className="text-[clamp(0.85rem,1.05vw,1.15rem)] leading-snug text-fg-secondary">
          min por cambio
          <br />
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {causal.eventos_count} cambio{causal.eventos_count === 1 ? '' : 's'} hoy
          </span>
        </span>
      </div>

      {sinEventos ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-[clamp(1rem,1.3vw,1.4rem)] text-fg-tertiary">
            Sin cambios registrados hoy
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col justify-start gap-[1.4vh]">
          {visibles.map((l) => (
            <FilaLider
              key={l.user_id}
              nombre={l.nombre}
              apellido={l.apellido}
              promedio={l.promedio_minutos}
              meta={causal.meta_minutos}
            />
          ))}
          {restantes > 0 && (
            <p className="text-[clamp(0.8rem,1vw,1.05rem)] text-fg-tertiary">
              +{restantes} líder{restantes === 1 ? '' : 'es'} más
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function PanelPits({ datos }: { datos: PantallaDatos }) {
  const causales = datos.pits;
  const totalEventos = causales.reduce((s, c) => s + c.eventos_count, 0);

  return (
    <section className="flex h-full flex-col gap-6 px-[4vw] py-[3vh]">
      <TituloPanel
        titulo="Entrada a Pits"
        subtitulo="Promedio de minutos por cambio · hoy (día operativo)"
      />
      {causales.length === 0 ? (
        <PanelVacio mensaje="Sin metas de Entrada a Pits configuradas — actívelas en Configuración" />
      ) : totalEventos === 0 ? (
        <PanelVacio mensaje="Sin cambios registrados hoy" />
      ) : (
        <>
          <div className="flex items-center gap-6 text-[clamp(0.8rem,1vw,1.05rem)] text-fg-secondary">
            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-[3px] w-4 rounded-sm"
                style={{ background: 'var(--warn)' }}
              />
              Meta
            </span>
            <span>Verde: dentro de meta · Rojo: por encima</span>
          </div>
          <div
            className={`grid min-h-0 flex-1 gap-[2vw] ${
              causales.length === 1 ? 'mx-auto w-full max-w-[58vw]' : ''
            }`}
            style={{
              gridTemplateColumns: `repeat(${Math.min(causales.length, 3)}, minmax(0, 1fr))`,
            }}
          >
            {causales.map((c) => (
              <ColumnaCausal key={c.causal_id} causal={c} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
